import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef
} from "react";
import { AuthContext } from "../../components/user/BCPAuthProvider";
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  InputAdornment,
  Link,
  SvgIcon,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";

import { postNotification } from "../../components/utilities/Notifications";

import { Formik } from "formik";
import * as Yup from "yup";

import GenericPicker from "../../components/utilities/GenericPicker";
import GoogleAddressSearch from "../../components/utilities/GoogleAddressSearch";
import ListResponsive from "../../components/other/ListResponsive";
import ModalLoadingFull from "../../components/modals/ModalLoadingFull";
import {
  cleanLocation,
  getData,
  getDistance
} from "../../components/utilities/UtilityFunctions";

import { GoogleMap, useLoadScript, Marker } from "@react-google-maps/api";
import { BCPSearch, BCPLocationDot } from "../../components/icons/bcp/BCPIcons";
import { getEnvironmentVariable } from "../../global/environmentVariables";
import OrganizationCardSmall from "../../components/layout/OrganizationCardSmall";
import {
  getLocalStorage,
  setLocalStorage
} from "../../components/layout/cookies/CookieFunctions";
import { useTranslation } from "react-i18next";

const libraries = ["places"];
const searchLimit = 100;
const orgTypes = [
  {
    label: "All",
    value: "all"
  },
  {
    label: "Game Store",
    value: "store"
  },
  {
    label: "Club",
    value: "club"
  }
];

export default function OrganizationSearch(props) {
  const { organizationType, distributorView, allowTypeSwitching } = props;
  const authContext = useContext(AuthContext);

  const { t } = useTranslation("translation", {
    keyPrefix: "components.OrganizationSearch"
  });

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: getEnvironmentVariable("GOOGLE_API_KEY"),
    libraries
  });

  var initalDistanceType = "miles";
  if (authContext?.authState?.cookieConsent) {
    var getGetCachedData = getLocalStorage(`distanceType`, authContext);
    if (getGetCachedData) {
      initalDistanceType = getGetCachedData;
    }
  }
  const [distanceType, setDistanceType] = useState(initalDistanceType);

  const [searchReady, setSearchReady] = useState(false);
  const [showSearch, setShowSearch] = useState(true);
  const [searchValues, setSearchValues] = useState({
    location: "Global",
    selectedAddress: "",
    selectedLocation: null,
    distance: 50,
    searchString: ""
  });

  const [searchLocation, setSearchLocation] = useState(null);
  const [geoLocation, setGeoLocation] = useState(null);

  const [_organizationType, _setOrganizationType] = useState(
    organizationType || "all"
  );

  const [organizations, setOrganizations] = useState(null);
  const [activeOrganization, setActiveOrganization] = useState(null);
  const [isSearching, setIsSearching] = useState(null);

  const [searchText, setSearchText] = useState("");

  const [center, setCenter] = useState({
    lat: 41.850033,
    lng: -87.6500523
  });

  const theme = useTheme();
  const mapRef = useRef();
  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const panTo = useCallback(({ lat, lng }) => {
    setCenter({ lat, lng });

    if (mapRef.current) {
      mapRef.current.panTo({ lat, lng });
      mapRef.current.setZoom(13);
    }
  }, []);

  const getOrganizations = async (filters, nextKey) => {
    const queryStringParameters = {
      limit: searchLimit,
      ...filters
    };
    if (nextKey) {
      queryStringParameters.nextKey = nextKey;
    }
    var apiGetData = await getData(`/gamestores`, {
      queryStringParameters: queryStringParameters
    });

    if (
      apiGetData.data?.length === searchLimit &&
      apiGetData.nextKey &&
      apiGetData.nextKey !== nextKey
    ) {
      var nextFetch = await getOrganizations(filters, apiGetData.nextKey);
      if (nextFetch.data?.legth > 0)
        apiGetData.data = [...apiGetData.data, ...nextFetch?.data];
    }

    return apiGetData.data;
  };

  const filter = (el) => {
    if (!searchText || searchText.trim() === "") return true;

    let filterText = searchText.trim();
    let regexQuotes = /"(.*?[^\\])"/g;
    var quoteTerms = [];
    var match;
    while ((match = regexQuotes.exec(filterText)) != null) {
      if (match[1]) {
        quoteTerms.push(match[1]);
      }
    }
    if (quoteTerms.length) {
      quoteTerms.forEach((quoteTerm) => {
        filterText = filterText.split(`"${quoteTerm}"`).join("");
      });
    }
    var terms = filterText.split('"').join("").split(" ");
    if (quoteTerms.length) {
      terms = [...terms, ...quoteTerms];
    }
    terms = terms.map((el) => el.trim()).filter((el) => el !== "");

    if (!terms.length) return true;

    for (let i = 0; i < terms.length; i++) {
      let term = terms[i].toLowerCase();
      if (
        !el?.name?.toLowerCase().includes(term) &&
        !el?.location?.name?.toLowerCase().includes(term)
      )
        return false;
    }

    return true;
  };

  useEffect(() => {
    const check = async () => {
      if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(function (position) {
          setGeoLocation(position.coords);
        });
      } else {
        setSearchValues({
          ...searchValues,
          location: "Global"
        });
      }

      setSearchReady(true);
    };
    check();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!isLoaded) return;

    panTo({
      lat:
        searchLocation?.center?.lat !== undefined
          ? searchLocation?.center?.lat
          : center.lat,
      lng:
        searchLocation?.center?.long !== undefined
          ? searchLocation?.center?.long
          : center.lng
    });
    // eslint-disable-next-line
  }, [props, isLoaded]);

  if (loadError) return t("errorLoadingMaps");
  if (!isLoaded) return <></>;

  return (
    <Box sx={{ width: "100%" }}>
      <Container
        sx={{
          pt: 2,
          pb: 2
        }}
        maxWidth="lg"
      >
        <Grid container sx={{ gap: 2 }}>
          <Grid item xs={12} sx={{ p: 2 }}>
            <Typography
              variant="h2"
              component="h3"
              fontWeight="700"
              gutterBottom
              sx={{ textTransform: "capitalize" }}
            >
              {_organizationType === "all"
                ? t("organization")
                : _organizationType}{" "}
              {t("finder")}
            </Typography>
          </Grid>
          {allowTypeSwitching && !isSearching && (
            <Grid item xs={12} sx={{ p: 2 }}>
              <ToggleButtonGroup value={_organizationType} exclusive fullWidth>
                {orgTypes.map((orgType) => (
                  <ToggleButton
                    key={orgType.value}
                    value={orgType.value}
                    onClick={() => {
                      _setOrganizationType(orgType.value);
                      if (!showSearch) {
                        setShowSearch(true);
                      }
                    }}
                  >
                    {orgType.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Grid>
          )}
          {showSearch && (
            <Grid item xs={12} sx={{ p: 2 }}>
              <Formik
                initialValues={searchValues}
                enableReinitialize={true}
                validationSchema={Yup.object().shape({
                  searchString: Yup.string()
                    .min(3, "Minimum 3 Characters!")
                    .max(255, "Maximum 255 Characters!")
                })}
                onSubmit={async (values, { setSubmitting, setFieldValue }) => {
                  try {
                    setIsSearching(true);
                    setOrganizations(null);
                    var filters = {};

                    if (
                      _organizationType !== undefined &&
                      _organizationType !== "all"
                    ) {
                      filters.organizationType = _organizationType;
                    }

                    var location;

                    if (values.location === "Current Location") {
                      location = {
                        distance: values.distance,
                        center: {
                          lat: geoLocation.latitude,
                          long: geoLocation.longitude
                        }
                      };
                    } else if (
                      values.location !== "Global" &&
                      values.selectedLocation?.coordinate?.length === 2
                    ) {
                      location = {
                        distance: values.distance,
                        center: {
                          lat: values.selectedLocation.coordinate[1],
                          long: values.selectedLocation.coordinate[0]
                        }
                      };
                    }

                    if (location) {
                      if (distanceType === "kms") {
                        location.distance = location.distance * 0.6214;
                      }
                      filters.location = JSON.stringify(location);
                      setSearchLocation(location);
                    }

                    if (values.searchString?.length > 0) {
                      filters.searchString = values.searchString.toLowerCase();
                    }

                    var apiGetData = await getOrganizations(filters, null);
                    if (apiGetData.length === 0) {
                      postNotification(
                        "info",
                        t("noOrganizationsFoundMatchingSearch")
                      );
                    } else {
                      apiGetData.forEach((organization) => {
                        if (organization.location?.coordinate) {
                          if (location?.center) {
                            organization.distanceType = distanceType;
                            organization.distance = getDistance(
                              organization.location.coordinate[1],
                              organization.location.coordinate[0],
                              {
                                latitude: location.center.lat,
                                longitude: location.center.long
                              }
                            );

                            if (distanceType === "kms") {
                              organization.distance =
                                organization.distance / 0.6214;
                            }
                          }
                        }
                      });

                      apiGetData.sort((a, b) => {
                        if (
                          a.distance !== undefined &&
                          b.distance !== undefined
                        ) {
                          return a.distance - b.distance;
                        }
                        if (a.distance !== undefined) {
                          return 1;
                        }
                        if (b.distance !== undefined) {
                          return -1;
                        }
                        return 0;
                      });
                    }

                    if (location) {
                      panTo({
                        lat:
                          location?.center?.lat !== undefined
                            ? location?.center?.lat
                            : center.lat,
                        lng:
                          location?.center?.long !== undefined
                            ? location?.center?.long
                            : center.lng
                      });
                    }

                    setOrganizations(apiGetData);
                    setIsSearching(false);
                    setShowSearch(false);
                  } catch (error) {
                    postNotification("error", error);
                    setIsSearching(false);
                  }
                }}
              >
                {({
                  errors,
                  handleBlur,
                  handleChange,
                  handleSubmit,
                  touched,
                  values,
                  setFieldValue
                }) => (
                  <form onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <GoogleAddressSearch
                          label={t("enterCityOrZipCode", {
                            keyPrefix: "fields"
                          })}
                          setLocationData={(e) => {
                            setFieldValue(
                              "selectedAddress",
                              e?.formatted_address ? e.formatted_address : ""
                            );
                            setFieldValue(
                              "location",
                              e?.formatted_address
                                ? e.formatted_address
                                : "Current Location"
                            );
                            setFieldValue("selectedLocation", e);
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          name="distance"
                          label={t("distanceIn", {
                            keyPrefix: "fields",
                            units: distanceType
                          })}
                          value={values.distance}
                          variant="outlined"
                          fullWidth
                          margin="normal"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(touched.distance && errors.distance)}
                          helperText={touched.distance && errors.distance}
                          type="number"
                          inputProps={{
                            min: 0
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <GenericPicker
                                  inclAny={false}
                                  list={["miles", "kms"]}
                                  value={distanceType}
                                  formControlSX={{
                                    p: 0,
                                    mt: 0,
                                    mb: 0,
                                    "& fieldset": {
                                      borderWidth: 0
                                    },
                                    "& .MuiOutlinedInput-root": {
                                      border: 0
                                    },
                                    "& .MuiSelect-select": {
                                      p: "4px"
                                    }
                                  }}
                                  handleChange={(e) => {
                                    setDistanceType(e.target.value);
                                    if (authContext?.authState?.cookieConsent) {
                                      try {
                                        setLocalStorage(
                                          "distanceType",
                                          e.target.value,
                                          authContext
                                        );
                                        var getCachedItems = getLocalStorage(
                                          "CachedItems",
                                          authContext
                                        );
                                        var cachedItems = getCachedItems
                                          ? JSON.parse(getCachedItems)
                                          : {};
                                        cachedItems["distanceType"] = true;
                                        setLocalStorage(
                                          "CachedItems",
                                          JSON.stringify(cachedItems),
                                          authContext
                                        );
                                      } catch (err) {}
                                    }
                                  }}
                                />
                              </InputAdornment>
                            )
                          }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          name="searchString"
                          label={`${t("organizationName", {
                            keyPrefix: "fields"
                          })} ${t("optional", {
                            keyPrefix: "fields"
                          })}`}
                          value={values.searchString}
                          variant="outlined"
                          fullWidth
                          margin="normal"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(
                            touched.searchString && errors.searchString
                          )}
                          helperText={
                            touched.searchString && errors.searchString
                          }
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Button
                          type="submit"
                          color={"primary"}
                          variant={"contained"}
                          fullWidth
                          startIcon={<BCPSearch />}
                        >
                          {t("search", {
                            keyPrefix: "buttons"
                          })}
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                )}
              </Formik>
            </Grid>
          )}
          {isSearching !== null && !showSearch && (
            <>
              <Grid
                item
                xs={12}
                sx={{
                  p: 2
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      sx={{
                        "& .MuiOutlinedInput-input": {
                          padding: 1
                        },
                        "& .MuiOutlinedInput-adornedEnd": {
                          paddingRight: 0
                        }
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="start">
                            <SvgIcon fontSize="small" color="action">
                              <BCPSearch />
                            </SvgIcon>
                          </InputAdornment>
                        )
                      }}
                      placeholder={t("searchResults")}
                      variant="outlined"
                      value={searchText}
                      onChange={(e) => {
                        setSearchText(e.target.value);
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      onClick={() => setShowSearch(true)}
                      startIcon={<BCPSearch />}
                    >
                      {t("newSearch", {
                        keyPrefix: "buttons"
                      })}
                    </Button>
                  </Grid>

                  {organizations?.length > 0 && (
                    <Grid item xs={12}>
                      <GoogleMap
                        id="map"
                        mapContainerStyle={{
                          width: "100%",
                          height: "50vh"
                        }}
                        zoom={5}
                        center={center}
                        options={{
                          disableDefaultUI: true,
                          zoomControl: true
                        }}
                        onLoad={onMapLoad}
                      >
                        {organizations
                          .filter(filter)
                          .map((organization, index) => {
                            if (!organization.location?.coordinate?.length) {
                              return <></>;
                            }

                            return (
                              <Marker
                                key={index}
                                position={{
                                  lat: organization.location.coordinate[1],
                                  lng: organization.location.coordinate[0]
                                }}
                                onClick={(e) => {
                                  setActiveOrganization(organization);
                                }}
                              />
                            );
                          })}
                      </GoogleMap>
                    </Grid>
                  )}
                </Grid>
              </Grid>

              <Grid
                item
                xs={12}
                sx={{
                  p: 2
                }}
              >
                <ListResponsive
                  originalList={
                    isSearching ? null : organizations?.filter(filter)
                  }
                  doPages
                  initialRowsPerPage={24}
                  rowsPerPageArray={[
                    12,
                    24,
                    48,
                    96,
                    {
                      label: t("all", {
                        keyPrefix: "components.ListResponsive"
                      }),
                      value: -1
                    }
                  ]}
                  rowProps={{
                    mapFunction: (row, index) => (
                      <Grid
                        key={index}
                        item
                        xs={12}
                        sm={6}
                        md={4}
                        sx={{ p: 1 }}
                      >
                        <OrganizationCardSmall
                          organization={row}
                          key={row.id}
                          distributorView={distributorView}
                        />
                      </Grid>
                    )
                  }}
                />
              </Grid>
            </>
          )}
        </Grid>

        {activeOrganization && (
          <Dialog
            open={true}
            onClose={() => {
              setActiveOrganization(null);
            }}
          >
            <DialogTitle>{`${activeOrganization.name}${
              activeOrganization.distance !== undefined
                ? ` (${
                    activeOrganization.distance > 10
                      ? parseInt(activeOrganization.distance)
                      : activeOrganization.distance.toFixed(2)
                  } ${t("miles", {
                    keyPrefix: "misc.units"
                  })})`
                : ""
            }`}</DialogTitle>
            <DialogContent>
              <DialogContentText>
                <Typography gutterBottom>
                  <Link
                    underline="none"
                    href={`https://google.com/maps/place/${
                      activeOrganization.location?.streetNum || ""
                    } ${activeOrganization.location?.streetName || ""} ${
                      activeOrganization.location?.city
                    } ${activeOrganization.location?.zip || ""} ${
                      activeOrganization.location?.state || ""
                    } ${activeOrganization.location?.country || ""}`.trim()}
                    target="_blank"
                  >
                    <Box
                      sx={{
                        minWidth: "35px",
                        verticalAlign: "bottom",
                        display: "inline-flex"
                      }}
                    >
                      <Tooltip title="Location">
                        <BCPLocationDot
                          style={{
                            color: theme?.palette?.text?.secondary,
                            "vertical-align": "bottom"
                          }}
                        />
                      </Tooltip>
                    </Box>
                    {cleanLocation(activeOrganization.location)}
                  </Link>
                </Typography>
              </DialogContentText>
            </DialogContent>
          </Dialog>
        )}
      </Container>
      {(isSearching || !searchReady) && <ModalLoadingFull />}
    </Box>
  );
}
